import React, {CSSProperties, ReactNode, useEffect} from 'react';
import {DropdownV2, noop} from '@priority-software/priority-style-react';
import {iSearchDropdownCloseKey} from '../../../../_legacy/FieldSearch/utils/keys';
import usePortal from 'react-useportal';
import {DropdownContent} from './DropdownnContent';
import {SearchHandlers, SearchState} from '../../../../hooks/dropdown/useSearch';
import {
    IsItemSelected,
    SelectHandlers
} from '../../../../components/Common/FieldSearch/hooks/useSelect';
import {Column} from '../../../../stores/Form/Column';
import {InputTrigger} from '../../../../_legacy/FieldSearch/components/InputTrigger';
import {isZoomable, zoom} from '../../actions/zoom';
import {keys} from '../../../../constants/KeyCodes';
import {DropdownInputType} from '../../../../components/Common/FieldSearch/types/DropdownProps';

export type Props = {
    children?: ReactNode;
    isOpen: boolean;
    onClose: () => void;
    column: Column;
    triggerRef?: any;
    isItemSelected: IsItemSelected;
    dropdownStyle: CSSProperties;
    resultsAutoMaxHeight?: number;
    dropdownInputType?: DropdownInputType;
    onZoomToAdvancedSearch?: () => void;
    onZoomToNewEntry?: () => void;
} & SearchState &
    SelectHandlers &
    SearchHandlers;

export const SearchDropdownV2 = (props: Props) => {
    const {Portal} = usePortal();

    const {
        onZoomToAdvancedSearch,
        onZoomToNewEntry,
        isOpen,
        onClose,
        column,
        handleNavigationKeys,
        handleSelectKeys,
        resultsAutoMaxHeight,
        dropdownStyle,
        dropdownInputType,
        ...contentProps
    } = props;

    const handleDropdownKeyDown = event => {
        handleNavigationKeys(event);
        handleSelectKeys(event);
    };

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', onClose);
            return () => window.removeEventListener('resize', onClose);
        }
        return noop;
    }, [onClose, isOpen]);

    useEffect(() => {
        if (isOpen) {
            const handleKeydown = async (e: KeyboardEvent) => {
                if (e.key === keys.F6) {
                    onClose();
                    if (isZoomable(column) && dropdownInputType === 'grid') {
                        await zoom(column.parentForm, column);
                    }
                }
            };
            window.addEventListener('keydown', handleKeydown);
            return () => window.removeEventListener('keydown', handleKeydown);
        }
        return noop;
    }, [onClose, isOpen, column, dropdownInputType]);

    if (!isOpen) {
        return null;
    }

    return (
        <Portal>
            <div className="reactComp">
                <DropdownV2
                    focusOnOpen
                    isOpen={isOpen}
                    onClose={onClose}
                    style={dropdownStyle}
                    trigger={
                        <InputTrigger onSearchChange={contentProps.handleSearchChange} {...props} />
                    }
                    isCloseKeyCallback={iSearchDropdownCloseKey}
                    onKeyDown={handleDropdownKeyDown}
                >
                    <DropdownContent
                        isItemSelected={contentProps.isItemSelected}
                        onSelectItem={contentProps.handleSelectItem}
                        onMultiSelect={contentProps.handleMultiSelect}
                        onClose={onClose}
                        isRelateKeyColumn={!!column.relatedIdColumn}
                        onIgnoreCaseChange={contentProps.handleIgnoreCaseChange}
                        onSearchFromBeginningChange={contentProps.handleSearchFromBeginningChange}
                        onScroll={contentProps.handleScroll}
                        onSearchByChange={() =>
                            contentProps.handleSearchByChange(contentProps.searchQuery)
                        }
                        onSortByChange={contentProps.handleSortByChange}
                        resultsAutoMaxHeight={resultsAutoMaxHeight}
                        onKeyDown={handleDropdownKeyDown}
                        //todo: should be updated with correct handlers
                        onZoomToAdvancedSearch={onZoomToAdvancedSearch}
                        onZoomToNewEntry={onZoomToNewEntry}
                        isZoom={false}
                        direction="auto"
                        hasStatus={column?.IsStatus}
                        statuses={column.parentForm.statuses}
                        {...contentProps}
                    />
                </DropdownV2>
            </div>
        </Portal>
    );
};
